import type { Node } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import type { CSSProperties, PropsWithChildren } from "react";
import { forwardRef, type ReactElement } from "react";
import type { TabListAria } from "react-aria";
import type { TabListState } from "react-stately";

import { useTab } from "../../hooks/useTab.ts";
import { Icon } from "../Icon/Icon.tsx";
import style from "./Tabs.module.css";

export interface TabBarProps extends TabListAria {
  state: TabListState<TabItemElement>;
  className?: string;
  style?: CSSProperties;
  asAction?: boolean;
}

const TabListBar = forwardRef<HTMLDivElement, TabBarProps>(function TabListBar(
  { state, tabListProps, className, ...props },
  ref,
) {
  return (
    <div
      {...tabListProps}
      className={clsx(style.tab_list, className, { [style.as_action]: props.asAction })}
      ref={ref}
      data-testid={"tabs"}
      style={props.style}
    >
      <div className={style.tabs_container}>
        {[...state.collection].map((item) => (
          <Tab key={item.key} item={item} state={state} />
        ))}
      </div>
    </div>
  );
});

export type TabItemElement = ReactElement<{ icon?: ReactElement | MaterialSymbol }>;

interface TabProps {
  item: Node<TabItemElement>;
  state: TabListState<ReactElement>;
}

/**
 * This is the tab, the button in the TabBar.
 * @param item The props will come from TabItemProps
 * @param state
 * @see TabItemProps
 */
const Tab = ({ item, state }: PropsWithChildren<TabProps>) => {
  const { ref, rendered, tabProps, isSelected, isDisabled } = useTab<HTMLDivElement>(state, item);

  return (
    <div
      {...tabProps}
      className={clsx(style.tab, { [style.tab_disabled]: isDisabled, [style.tab_selected]: isSelected })}
      ref={ref}
      data-testid={"tab"}
    >
      {!!item.props.icon && <Icon icon={item.props.icon} className={style.tab_icon} />}
      {rendered}
    </div>
  );
};

export { TabListBar };
